import { useRef } from 'react';

import { FilePreview, NavigationControl } from '@proton/components';
import { useSharingModal } from '@proton/drive/modules/sharingModal';

import { useFlagsDriveSheet } from '../../flags/useFlagsDriveSheet';
import { useDetailsModal } from '../../modals/DetailsModal';
import type { Drive } from './interface';
import { isDriveWithSharing } from './interface';
import { SignatureInformation, SignatureStatus } from './signatures';
import { usePreviewState } from './usePreviewState';

export interface PreviewProps {
    nodeUid: string;
    revisionUid?: string;
    // Entire list of previewable images in the current view
    previewableNodeUids?: string[];
    drive: Drive;
    onNodeChange?: (nodeUid: string) => void;
    verifySignatures?: boolean;
    canOpenInDocs?: boolean;
    canOpenDetails?: boolean;
    onClose: () => void;
    date?: Date;

    /**
     * @deprecated It is to connect preview to the legacy Drive photo section.
     * Once the Photos SDK is fully ready, we will remove this and use SDK directly.
     */
    photos?: PhotosProps;
}

type PhotosProps = {
    date?: number;
    isFavorite?: boolean;
    onFavorite?: () => void;
    onSelectCover?: () => void;
    isForPhotos?: boolean;
};

export function Preview({
    drive,
    nodeUid,
    revisionUid,
    previewableNodeUids,
    onNodeChange,
    verifySignatures = true,
    canOpenInDocs = true,
    canOpenDetails = true,
    onClose,
    date,
    photos,
}: PreviewProps) {
    const preview = usePreviewState({
        drive,
        nodeUid,
        revisionUid,
        previewableNodeUids,
        onNodeChange,
        verifySignatures,
    });

    // Ensure Photos version of the preview is used in the photo section.
    // Handle automatically once Photos SDK is used directly.
    if (photos) {
        photos.isForPhotos = true;
    }

    const { detailsModal, showDetailsModal } = useDetailsModal();

    const sheetsEnabled = useFlagsDriveSheet();

    const { sharingModal, showSharingModal } = useSharingModal();

    const rootRef = useRef<HTMLDivElement>(null);

    const onDetails = () => {
        showDetailsModal({ nodeUid: preview.node.nodeUid, drive });
    };
    const sharingDrive = isDriveWithSharing(drive) ? drive : undefined;
    const onShare =
        preview.canShare && sharingDrive
            ? () => showSharingModal({ nodeUid: preview.node.nodeUid, drive: sharingDrive })
            : undefined;

    return (
        <>
            <FilePreview
                ref={rootRef}
                isMetaLoading={preview.isLoading}
                isLoading={preview.isContentLoading}
                error={preview.errorMessage}
                contents={preview.content.data}
                fileName={preview.node.name}
                mimeType={preview.node.mediaType}
                sharedStatus={preview.node.sharedStatus}
                fileSize={preview.node.displaySize}
                onClose={onClose}
                onDownload={preview.actions.downloadFile}
                videoStreaming={preview.content.videoStreaming}
                onSave={preview.actions.saveFile}
                onDetails={canOpenDetails ? onDetails : undefined}
                onShare={onShare}
                onOpenInDocs={canOpenInDocs ? preview.actions.openInDocs : undefined}
                imgThumbnailUrl={preview.content.thumbnailUrl}
                navigationControls={
                    preview.navigation && (
                        <NavigationControl
                            current={preview.navigation.currentPosition}
                            total={preview.navigation.totalCount}
                            rootRef={rootRef}
                            onPrev={preview.navigation.loadPrevious}
                            onNext={preview.navigation.loadNext}
                        />
                    )
                }
                signatureStatus={<SignatureStatus contentSignatureIssue={preview.node.contentSignatureIssueLabel} />}
                signatureConfirmation={
                    preview.node.contentSignatureIssueLabel && (
                        <SignatureInformation contentSignatureIssue={preview.node.contentSignatureIssueLabel} />
                    )
                }
                sheetsEnabled={sheetsEnabled}
                date={date}
                {...photos}
            />
            {detailsModal}
            {sharingModal}
        </>
    );
}
